import {
  createFrontendPlugin,
  createRouteRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import SchoolIcon from '@material-ui/icons/School';

/**
 * The guide pages live in `packages/app/src/components/guide/` and used to be
 * mounted by hand in the legacy `FlatRoutes`. They are wired to the new
 * frontend system here.
 *
 * **Why a plugin and not a `createFrontendModule({ pluginId: 'app' })`.** The
 * extension id of a page is `page:<pluginId>` when it has no name and
 * `page:<pluginId>/<name>` when it has one. Grouping the pages under their own
 * plugin id therefore buys three things a module attached to `app` would not:
 *
 * - the index page is `page:guide`, which is the id `modules/nav/Sidebar.tsx`
 *   already reserves with `nav.take('page:guide')` — the sidebar entry works
 *   without touching the sidebar;
 * - `title` and `icon` are declared once, on the plugin, and
 *   `PageBlueprint` falls back to them for every page that does not set its
 *   own — so the eleven sub-pages get the same icon in their `PluginHeader`,
 *   and the sidebar entry gets its label and icon from the same place;
 * - the ids read `page:guide/manifesto` rather than `page:app/guide-manifesto`,
 *   which is what `app.extensions` in `app-config.yaml` has to name to disable
 *   or re-path a single guide.
 *
 * **Nav entries.** `AppNav` builds a sidebar item only for a page that has a
 * route ref *and* a title *and* an icon. Only the index page has a route ref,
 * so it is the only one in the sidebar; the sub-pages are reached from its
 * cards, from the home page and from each other by path, exactly as before.
 * That is also why the sub-pages are named but route-ref-less: nothing resolves
 * them through `useRouteRef`.
 *
 * **Titles.** Every sub-page declares the title its legacy MUI `Header` used to
 * show, because that is what the `PluginHeader` renders now and what the
 * breadcrumb carries. The index page deliberately does *not*: leaving it unset
 * makes `PageBlueprint` fall back to the plugin title, which keeps the header
 * and the sidebar label spelled the same way in one place.
 */
export const guideRouteRef = createRouteRef();

const guidesIndexPage = PageBlueprint.make({
  params: {
    path: '/guide',
    routeRef: guideRouteRef,
    loader: () =>
      import('../../components/guide/GuidesIndexPage').then(m => (
        <m.GuidesIndexPage />
      )),
  },
});

const gettingStartedPage = PageBlueprint.make({
  name: 'getting-started',
  params: {
    path: '/guide/oneplatform-getting-started',
    title: 'Getting started',
    loader: () =>
      import('../../components/guide/GettingStarted').then(m => (
        <m.GettingStarted />
      )),
  },
});

const onboardingPage = PageBlueprint.make({
  name: 'onboarding',
  params: {
    path: '/guide/oneplatform-onboarding',
    title: 'OnePlatform Onboarding',
    loader: () =>
      import('../../components/guide/OneplatformOnBoardingPage').then(m => (
        <m.OnePlatformOnboardingPage />
      )),
  },
});

const legacyResourcesPage = PageBlueprint.make({
  name: 'legacy-resources',
  params: {
    path: '/guide/legacy-resources',
    title: 'Legacy & Business as Usual',
    loader: () =>
      import('../../components/guide/LegacyResourcesPage').then(m => (
        <m.LegacyResourcesPage />
      )),
  },
});

const designPrinciplesPage = PageBlueprint.make({
  name: 'design-principles',
  params: {
    path: '/guide/design-principles',
    title: 'Design Principles',
    loader: () =>
      import('../../components/guide/DesignPrinciplesPage').then(m => (
        <m.DesignPrinciplesPage />
      )),
  },
});

const manifestoPage = PageBlueprint.make({
  name: 'manifesto',
  params: {
    path: '/guide/manifesto',
    title: 'OnePlatform Manifesto',
    loader: () =>
      import('../../components/guide/ManifestoPage').then(m => (
        <m.ManifestoPage />
      )),
  },
});

const citymapGuidedTourPage = PageBlueprint.make({
  name: 'citymap-guided-tour',
  params: {
    path: '/guide/citymap-guided-tour',
    title: 'Citymap Guided Tour',
    loader: () =>
      import('../../components/guide/CityMapGuidedTourPage').then(m => (
        <m.CityMapGuidedTourPage />
      )),
  },
});

const developerViewPage = PageBlueprint.make({
  name: 'developer-view',
  params: {
    path: '/guide/developer-view',
    title: 'Developer View',
    loader: () =>
      import('../../components/guide/DeveloperViewPage').then(m => (
        <m.DeveloperViewPage />
      )),
  },
});

const productOwnerViewPage = PageBlueprint.make({
  name: 'product-owner-view',
  params: {
    path: '/guide/product-owner-view',
    title: 'Product Owner View',
    loader: () =>
      import('../../components/guide/ProductOwnerViewPage').then(m => (
        <m.ProductOwnerViewPage />
      )),
  },
});

/**
 * The three guideline pages keep their legacy `/guidelines/*` paths, camel case
 * included: they are linked by path from the guides index and from Confluence
 * and Teams messages outside the portal.
 */
const publicCloudGovernancePage = PageBlueprint.make({
  name: 'public-cloud-governance',
  params: {
    path: '/guidelines/publicCloudGovernance',
    title: 'Public Cloud Governance',
    loader: () =>
      import('../../components/guide/PublicCloudGovernance').then(m => (
        <m.PublicCloudGovernance />
      )),
  },
});

const openSourceLicensesPage = PageBlueprint.make({
  name: 'open-source-licenses',
  params: {
    path: '/guidelines/openSourceLicenses',
    title: 'Open Source Licenses Utilization',
    loader: () =>
      import('../../components/guide/OpenSourceLicences').then(m => (
        <m.OpenSourceLicences />
      )),
  },
});

const securityGuidelinesPage = PageBlueprint.make({
  name: 'security',
  params: {
    path: '/guidelines/security',
    title: 'Security',
    loader: () =>
      import('../../components/guide/Security').then(m => <m.Security />),
  },
});

export const guidePlugin = createFrontendPlugin({
  pluginId: 'guide',
  title: 'Guides & Knowledge',
  icon: <SchoolIcon fontSize="inherit" />,
  routes: {
    root: guideRouteRef,
  },
  extensions: [
    guidesIndexPage,
    gettingStartedPage,
    onboardingPage,
    legacyResourcesPage,
    designPrinciplesPage,
    manifestoPage,
    citymapGuidedTourPage,
    developerViewPage,
    productOwnerViewPage,
    publicCloudGovernancePage,
    openSourceLicensesPage,
    securityGuidelinesPage,
  ],
});
